import Phaser from "phaser";
import { NineSliceContainer, TextMaxWidth } from "@ravalmatic/catafractum-utils";
import { Preferences } from "../../Preferences";
import { Language } from "../../PersistentDataManager";

export class LanguageSelectionLayer extends Phaser.GameObjects.Container {
    private static readonly MENU_WIDTH = 900;
    private static readonly MENU_HEIGHT = 760;
    private static readonly MENU_BORDER_SIZE = 80;
    private static readonly COLUMNS = 2;
    private static readonly COLUMN_SPACING = 260;
    private static readonly FIRST_ROW_Y = -225;

    private readonly darkLayer: Phaser.GameObjects.Rectangle;
    private readonly menuContainer: Phaser.GameObjects.Container;

    constructor(scene: Phaser.Scene, onClose: () => void) {
        super(scene);
        this.setDepth(3000);

        this.darkLayer = new Phaser.GameObjects.Rectangle(scene, 0, 0,
            scene.scale.width, scene.scale.height, 0x000000, .5)
            .setOrigin(0).setInteractive();
        this.darkLayer.on(Phaser.Input.Events.POINTER_UP, onClose);
        this.add(this.darkLayer);

        this.menuContainer = new Phaser.GameObjects.Container(scene);
        this.add(this.menuContainer);
        const background = new NineSliceContainer(scene, 0, 0,
            "texture_atlas_1", "bg_instructions",
            LanguageSelectionLayer.MENU_WIDTH, LanguageSelectionLayer.MENU_HEIGHT,
            LanguageSelectionLayer.MENU_BORDER_SIZE);
        background.setInteractive(new Phaser.Geom.Rectangle(
            -LanguageSelectionLayer.MENU_WIDTH / 2, -LanguageSelectionLayer.MENU_HEIGHT / 2,
            LanguageSelectionLayer.MENU_WIDTH, LanguageSelectionLayer.MENU_HEIGHT
        ), Phaser.Geom.Rectangle.Contains);
        background.on(Phaser.Input.Events.POINTER_UP,
            (_pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) =>
                event.stopPropagation());
        this.menuContainer.add(background);

        const title = new TextMaxWidth(scene, 0, -325, {
            key: Preferences.language === "es" ? "SELECCIONAR IDIOMA" : "SELECT LANGUAGE",
            style: {
                fontFamily: "Baloo2-Bold", fontSize: "54px", fontStyle: "bold",
                color: "#F7D75C", stroke: "#4A3218", strokeThickness: 6
            },
            maxSize: { maxWidth: LanguageSelectionLayer.MENU_WIDTH - 100, maxHeight: 65 }
        }).setOrigin(.5);
        this.menuContainer.add(title);

        (["en", "es"] as const).forEach((language, index) => {
            this.menuContainer.add(this.createLanguageOption(scene,
                (index - (LanguageSelectionLayer.COLUMNS - 1) / 2) * LanguageSelectionLayer.COLUMN_SPACING,
                LanguageSelectionLayer.FIRST_ROW_Y, language,
                language === "en" ? "English" : "Español", onClose));
        });
        this.relayout();
        this.setAlpha(0);
        scene.tweens.add({
            targets: this, alpha: 1, duration: 250, ease: Phaser.Math.Easing.Cubic.Out
        });
    }

    public relayout(): void {
        const { width, height } = this.scene.scale;
        this.darkLayer.setSize(width, height);
        this.menuContainer.setPosition(width / 2, height / 2);
    }

    private createLanguageOption(
        scene: Phaser.Scene, x: number, y: number, language: Language, label: string, onClose: () => void
    ): Phaser.GameObjects.Container {
        const button = new Phaser.GameObjects.Container(scene, x, y);
        const selected = Preferences.language === language;
        const radioOuter = new Phaser.GameObjects.Arc(scene, -100, 0, 19, 0, 360, false, 0xFFF4DC)
            .setStrokeStyle(4, 0x4A3218);
        const radioInner = new Phaser.GameObjects.Arc(scene, -100, 0, 10, 0, 360, false,
            0x168F67, selected ? 1 : 0);
        const text = new Phaser.GameObjects.Text(scene, -70, 0, label, {
            fontFamily: "Baloo2-Bold", fontSize: "28px", color: selected ? "#087A5B" : "#4A3218"
        }).setOrigin(0, .5);
        text.setScale(Math.min(1, 190 / text.width));
        button.add([radioOuter, radioInner, text]);
        button.setSize(240, 68).setInteractive({ useHandCursor: true });
        button.on(Phaser.Input.Events.POINTER_OVER, () => button.setScale(1.04));
        button.on(Phaser.Input.Events.POINTER_OUT, () => button.setScale(1));
        button.on(Phaser.Input.Events.POINTER_UP, () => {
            if (Preferences.language !== language) Preferences.setLanguage(language);
            onClose();
        });
        return button;
    }
}
